import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useAuth } from "../../Context/AuthContext";
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Box,
    CircularProgress,
    Alert,
} from "@mui/material";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface MetricValue {
    timeStamp: string;
    value: number;
}

interface Metric {
    metricName: string;
    values: MetricValue[];
}

const MetricsView: React.FC = () => {
    const { authService } = useAuth();
    const [timeframe, setTimeframe] = useState<string>("1hour");
    const [metrics, setMetrics] = useState<Metric[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchMetrics = async () => {
        setLoading(true);
        setError(null);
        setMetrics([]);
        try {
            const response = await authService.get<Metric[]>(
                `metricsApi/GetMetrics/?timeframe=${timeframe}`
            );
            setMetrics(response);
        } catch (err: any) {
            setError(err.response?.data || "An error occurred while fetching metrics.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
    }, []);

    const getChartData = (metric: Metric) => {
        const labels = metric.values.map((value) =>
            new Date(value.timeStamp).toLocaleTimeString([], {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit' })
        );
        const dataPoints = metric.values.map((value) => value.value);

        return {
            labels,
            datasets: [
                {
                    label: `${metric.metricName} Usage (%)`,
                    data: dataPoints,
                    fill: false,
                    borderColor: "rgba(75, 192, 192, 1)",
                    tension: 0.1,
                },
            ],
        };
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                min: 0, // Start Y-axis at 0
                max: 100, // End Y-axis at 100
            },
        },
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Database Metrics
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mb={4}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="timeframe-label">Select Timeframe</InputLabel>
                    <Select
                        labelId="timeframe-label"
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        label="Select Timeframe"
                    >
                        <MenuItem value="1hour">Last 1 Hour</MenuItem>
                        <MenuItem value="1day">Last 1 Day</MenuItem>
                        <MenuItem value="7days">Last 7 Days</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" color="primary" onClick={fetchMetrics}>
                    Fetch Metrics
                </Button>
            </Box>
            {loading && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            )}
            <Box>
                {metrics.map((metric, index) => (
                    <Box key={index} mb={4}>
                        <Typography variant="h6" gutterBottom>
                            {metric.metricName}
                        </Typography>
                        <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}>
                            <Line
                                data={getChartData(metric)}
                                options={chartOptions}
                                height={300}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Container>
    );
};

export default MetricsView;
